import React from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import useSeo from "../hooks/useSeo";
import { useResults } from "../hooks/useApi";

const EXTERNAL_URL = "https://shillongmorningteerresulttoday.com/";

const boxes = [
  { key: "common", img: "/tiles/common-numbers.webp", to: "/common-numbers" },
  { key: "banner", img: "/tiles/banner1.png", to: "/" },
  { key: "dream", img: "/tiles/dream-numbers.webp", to: "/dream-numbers" },
  { key: "analytics", img: "/tiles/Predict-analysis.webp", to: "/analytics" },
  { key: "predict", img: "/tiles/targetgame.webp", to: "/predict-target" },
  { key: "previous", img: "/tiles/previous-numbers.webp", to: "/previous-result" },
  { key: "calendar", img: "/tiles/TeerCalendar.webp", to: "/calendar" },
  { key: "counter", img: "/tiles/ReputedCounter.webp", to: "/teer-counter" },
  { key: "shillong", img: "/tiles/shillong-counter.jpg", external: EXTERNAL_URL },
];

const GridBox = ({ box, onClick }) => (
  <button
    onClick={onClick}
    className="relative aspect-square rounded-sm overflow-hidden transition-transform duration-200 hover:scale-[1.03] hover:shadow-lg"
  >
    <img src={box.img} alt={box.key} className="w-full h-full object-cover" loading="lazy" />
  </button>
);

// today's date in IST as dd-mm-yyyy
const todayIST = () => {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date());
  return p.replace(/\//g, "-");
};

const Home = () => {
  const navigate = useNavigate();
  const { data: results = [] } = useResults();

  useSeo(
    "Morning Sunday Teer Result | Shillong Morning Teer Result",
    "Morning Sunday Teer gives today's Shillong Morning Teer Result with FR & SR numbers, plus previous results, common number and dream number updates."
  );

  // Show the latest published result until admin publishes a newer one.
  const latest = results[0] || null;
  const previous = results.slice(0, 7);
  const today = todayIST();

  const handleBox = (box) => {
    if (box.external) window.location.href = box.external;
    else navigate(box.to);
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Single H1 */}
        <h1 className="text-center text-2xl font-bold text-[#1f2a37] mb-1">
          Morning Sunday Teer Result Today
        </h1>
        <p className="text-center text-base font-semibold text-[#3aa79a] mb-4">
          {today.replace(/-/g, "/")}
        </p>

        {/* Today's result / waiting state */}
        <section id="morning-sunday-teer-result" className="scroll-mt-24">
          <div className="border-2 border-black rounded-sm overflow-hidden mb-3 max-w-2xl mx-auto">
            <div className="bg-[#e6e6e6] text-center py-2 text-lg font-medium border-b-2 border-black">
              SHILLONG
            </div>
            <div className="grid grid-cols-2">
              <div className="bg-[#7fd0b8] text-center py-2 font-semibold border-r-2 border-b-2 border-black">
                F/R(10:30 AM)
              </div>
              <div className="bg-[#7fd0b8] text-center py-2 font-semibold border-b-2 border-black">
                S/R(11:30 AM)
              </div>
              <div className="text-center py-2 text-xl font-bold border-r-2 border-black">
                {latest ? latest.fr : "--"}
              </div>
              <div className="text-center py-2 text-xl font-bold">
                {latest ? latest.sr : "--"}
              </div>
            </div>
          </div>
          <div className="mb-8" />
        </section>

        {/* 3x3 grid */}
        <div className="grid grid-cols-3 gap-1.5 mb-10">
          {boxes.map((b) => (
            <GridBox key={b.key} box={b} onClick={() => handleBox(b)} />
          ))}
        </div>

        {/* Content sections */}
        <div className="space-y-8 text-[#374151] leading-relaxed">
          <section id="morning-sunday-teer" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold text-[#1f2a37]">Morning Sunday Teer</h2>
            <p>
              <b>Morning Sunday Teer</b> is the Sunday morning session of the
              traditional Shillong Teer, an archery based number game played in
              Shillong, Meghalaya. Two rounds are held each session — the First
              Round (F/R) and the Second Round (S/R) — and the winning number is
              taken from the official count of arrows that hit the target.
            </p>
          </section>

          <section id="shillong-morning-teer-result" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold text-[#1f2a37]">
              Shillong Morning Teer Result Today
            </h2>
            <p>
              The Shillong Morning Teer Result for today is shown in the table at
              the top of this page. The First Round is usually declared around
              10:30 AM and the Second Round around 11:30 AM (IST). Until today's
              numbers are published you will see a clear waiting state instead of
              an older result.
            </p>
          </section>

          <section id="morning-teer-fr-sr" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold text-[#1f2a37]">
              Morning Sunday Teer FR &amp; SR Result
            </h2>
            <p>
              Each Morning Teer session gives two winning numbers. The FR result
              is the First Round number and the SR result is the Second Round
              number. Both are two digit numbers between 00 and 99, decided by
              the total arrow count for that round.
            </p>
          </section>

          <section id="sunday-morning-teer-result" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold text-[#1f2a37]">
              Sunday Morning Teer Result
            </h2>
            <p>
              Because this is the Sunday morning session, many people search for
              the Sunday Morning Teer Result specifically. It follows the same
              rules as the regular Shillong Teer and is updated here on the same
              day the numbers are declared.
            </p>
          </section>

          {/* Previous results */}
          <section id="previous-morning-teer-results" className="scroll-mt-24">
            <h2 className="text-lg font-bold text-[#1f2a37] mb-3">
              Previous Morning Teer Results
            </h2>
            <div className="border border-gray-300 rounded-md overflow-hidden">
              <table className="w-full text-center text-sm">
                <thead>
                  <tr className="bg-[#3aa79a] text-white">
                    <th className="py-2 font-semibold">Date</th>
                    <th className="py-2 font-semibold">F/R</th>
                    <th className="py-2 font-semibold">S/R</th>
                  </tr>
                </thead>
                <tbody>
                  {previous.map((r, i) => (
                    <tr key={r.id} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                      <td className="py-2 border-t border-gray-200">
                        {r.date.replace(/-/g, "/")}
                      </td>
                      <td className="py-2 border-t border-gray-200 font-semibold">{r.fr}</td>
                      <td className="py-2 border-t border-gray-200 font-semibold">{r.sr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3">
              View the full history on the{" "}
              <Link to="/previous-result" className="text-[#2f5d84] font-medium hover:underline">
                Morning Teer Previous Result
              </Link>{" "}
              page.
            </p>
          </section>

          <section id="morning-teer-common-number" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold text-[#1f2a37]">
              Morning Teer Common Number
            </h2>
            <p>
              A common number is a suggested target based on historical analysis
              of past results. See today's{" "}
              <Link to="/common-numbers" className="text-[#2f5d84] font-medium hover:underline">
                Morning Teer Common Number
              </Link>{" "}
              with Direct, House and Ending values. These are references only, not
              guaranteed outcomes.
            </p>
          </section>

          <section id="morning-teer-dream-number" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold text-[#1f2a37]">
              Morning Teer Dream Number
            </h2>
            <p>
              Dream numbers come from a traditional practice of linking dream
              symbols to numbers. Explore the full chart on the{" "}
              <Link to="/dream-numbers" className="text-[#2f5d84] font-medium hover:underline">
                Morning Teer Dream Number
              </Link>{" "}
              page, or read more on{" "}
              <Link to="/about" className="text-[#2f5d84] font-medium hover:underline">
                About
              </Link>{" "}
              and{" "}
              <Link to="/contact" className="text-[#2f5d84] font-medium hover:underline">
                Contact
              </Link>
              .
            </p>
          </section>

          <p className="text-xs text-gray-500 italic">
            Disclaimer: This website is an independent, informational platform. It
            is not affiliated with any official Teer organizing body and does not
            sell tickets, accept bets or process payments. Numbers shown are for
            information only and are not guaranteed outcomes. Please play
            responsibly.
          </p>

          {/* Quick Links — hidden visually, kept in DOM for search engines */}
          <nav aria-label="Quick Links" className="sr-only">
            <h2>Quick Links</h2>
            <ul>
              <li><a href="#morning-sunday-teer" className="block text-sm text-[#2f5d84] hover:underline">Morning Sunday Teer</a></li>
              <li><a href="#morning-sunday-teer-result" className="block text-sm text-[#2f5d84] hover:underline">Morning Sunday Teer Result</a></li>
              <li><a href="#sunday-morning-teer-result" className="block text-sm text-[#2f5d84] hover:underline">Sunday Morning Teer Result</a></li>
              <li><Link to="/previous-result" className="block text-sm text-[#2f5d84] hover:underline">Previous Result</Link></li>
              <li><Link to="/common-numbers" className="block text-sm text-[#2f5d84] hover:underline">Common Number</Link></li>
              <li><Link to="/dream-numbers" className="block text-sm text-[#2f5d84] hover:underline">Dream Number</Link></li>
            </ul>
          </nav>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
